import { apiVision } from "./api-vision";
import { isAxiosError } from "axios";
import type {
  TicketRequestPayload,
  ListProposalsResponse,
  ProposalListItem,
  ProposalListParams,
  QuickSearchLogPayload,
} from "./types";
import { z } from "zod";
import { CreateLongListPayload, ListExecutivesResponse } from "./types";
import type {
  ImportDealBriefStatus,
  ImportDealResponse,
  PointOfInterest,
  POICategory,
  RankedSpace,
  SearchAndRankBody,
  CreateProposalBody,
  CreateProposalResponse,
  ProposalPayload,
  GetProposalApiResponseV2,
  SearchSource,
  UpdateProposalBody,
  UpdateProposalResult,
} from "@/features/vision-360/ai-mode/types";
import { VersionConflictError } from "@/features/vision-360/ai-mode/errors";
import { reportError } from "@/utils/observability";
import { reconstructRoomCombinations } from "@/features/vision-360/ai-mode/utils/comboGrouping";
import { privateOfficeService } from "@/services/private-office-service";
import { PrivateOfficeApprovalStatus } from "@/services/private-office-service/types";
import type {
  ResultItem,
  SearchPrivateOfficesParams,
} from "@/services/private-office-service/types";
import {
  isFiltersResultOutdated,
  mapFiltersResultToRankedSpaces,
  mapSearchResultToRankedSpaces,
} from "./mappers/filters-to-ranked-space";
import {
  mapNewFormatProposal,
  mapSpaceRatings,
  parseEmployeeCount,
} from "./mappers/proposal-from-api";
import { getCategoryIdFromPropertyType } from "@/features/vision-360/constants/property-types";
import { getVipOfficePricePerPosition } from "@/features/vision-360/constants/vip-office-price-table";

export function getDefaultSearchSource(): SearchSource {
  return "search";
}

function shouldUseFiltersEndpoint(searchSource?: SearchSource | null): boolean {
  if (searchSource === "filters") return true;
  return false;
}

const longListDetailSchema = z.object({
  id: z.string(),
  demands: z.number().nullable().optional(),
  spaces_returned: z.number().nullable().optional(),
  spaces_returned_ids: z.array(z.number()).nullable().optional(),
  private_rooms_returned_ids: z.array(z.string()).nullable().optional(),
  demands_without_results: z.number().nullable().optional(),
  total_results: z.number().nullable().optional(),
  results: z
    .array(
      z.object({
        demand_name: z.string().optional(),
        match_percent: z.string().nullable().optional(),
        search_id: z.number().nullable().optional(),
        space_id: z.string().nullable().optional(),
        private_room_id: z.string().nullable().optional(),
        label: z.string().optional(),
        capacity: z.number().nullable().optional(),
        monthly_price: z.number().nullable().optional(),
        monthly_credit_price: z.number().nullable().optional(),
        size: z.number().nullable().optional(),
        dpp: z.string().nullable().optional(),
        status: z.union([z.string(), z.number()]).nullable().optional(),
        space: z
          .object({
            id: z.union([z.number(), z.string()]),
            name: z.string(),
            neighborhood: z.string().optional(),
            cityname: z.string().optional(),
          })
          .optional(),
        details: z
          .object({
            id: z.union([z.number(), z.string()]).nullable().optional(),
            label: z.string().nullable().optional(),
            picture_url: z.string().nullable().optional(),
            picture_url1: z.string().nullable().optional(),
            picture_url2: z.string().nullable().optional(),
            picture_url3: z.string().nullable().optional(),
            picture_url4: z.string().nullable().optional(),
            picture_url5: z.string().nullable().optional(),
            updated_at: z.string().nullable().optional(),
            search_id: z.number().nullable().optional(),
            space_id: z.union([z.number(), z.string()]).nullable().optional(),
            size: z.number().nullable().optional(),
            capacity: z.number().nullable().optional(),
            monthly_price: z.number().nullable().optional(),
            status: z.number().nullable().optional(),
          })
          .nullable()
          .optional(),
        space_details: z
          .object({
            id: z.union([z.number(), z.string()]).optional(),
            name: z.string().optional(),
            address: z.string().nullable().optional(),
            neighborhood: z.string().nullable().optional(),
            cityname: z.string().nullable().optional(),
            latitude: z.string().nullable().optional(),
            longitude: z.string().nullable().optional(),
            enabled: z.boolean().nullable().optional(),
            type: z.number().nullable().optional(),
          })
          .nullable()
          .optional(),
        detailed_score_analysis: z
          .array(
            z.object({
              criterion: z.string(),
              requested: z.any(),
              offered: z.any(),
              matched: z.boolean(),
            }),
          )
          .nullable()
          .optional(),
        score_details: z.any().optional(),
      }),
    )
    .optional(),
});

export type LongListDetailResponse = z.infer<typeof longListDetailSchema>;

const longListResponseSchema = z.object({
  id: z.string(),
  credits_price: z.number(),
  extra_value: z.number().nullable().optional(),
  items_count: z.number().optional(),
  items: z
    .array(
      z.object({
        id: z.string(),
        label: z.string(),
        space: z.object({
          id: z.string(),
          name: z.string(),
          neighborhood: z.string(),
          cityname: z.string(),
        }),
        capacity: z.number(),
        size: z.number().nullable(),
        monthly_price: z.number(),
        credits: z.number(),
        take: z.number(),
        client_price: z.number().nullable().optional(),
        extra_credits: z.number().nullable().optional(),
        total_price: z.number().nullable().optional(),
        total_credits: z.number().nullable().optional(),
        margin: z.number().nullable().optional(),
        extra_value: z.number().nullable().optional(),
        search_id: z.number().nullable().optional(),
        space_id: z.string().nullable().optional(),
        private_room_id: z.string().nullable().optional(),
        match_percent: z.string().nullable().optional(),
        monthly_credit_price: z.number().nullable().optional(),
        dpp: z.string().nullable().optional(),
        status: z.union([z.string(), z.number()]).nullable().optional(),
        days_since_update: z.string().nullable().optional(),
        distance_km: z.number().nullable().optional(),
        amenities: z.string().nullable().optional(),
        details: z
          .object({
            id: z.union([z.number(), z.string()]).nullable().optional(),
            search_id: z.number().nullable().optional(),
            space_id: z.union([z.number(), z.string()]).nullable().optional(),
            label: z.string().nullable().optional(),
            size: z.number().nullable().optional(),
            capacity: z.number().nullable().optional(),
            monthly_price: z.number().nullable().optional(),
            yearly_price: z.number().nullable().optional(),
            amenities: z.string().nullable().optional(),
            extra_services: z.string().nullable().optional(),
            shared_amenities: z.string().nullable().optional(),
            shared_extra_services: z.string().nullable().optional(),
            advantages: z.string().nullable().optional(),
            picture_url: z.string().nullable().optional(),
            picture_url1: z.string().nullable().optional(),
            picture_url2: z.string().nullable().optional(),
            picture_url3: z.string().nullable().optional(),
            picture_url4: z.string().nullable().optional(),
            picture_url5: z.string().nullable().optional(),
            notes: z.string().nullable().optional(),
            status: z.number().nullable().optional(),
            created_at: z.string().nullable().optional(),
            updated_at: z.string().nullable().optional(),
            deleted_at: z.string().nullable().optional(),
            created_by_user_id: z.number().nullable().optional(),
          })
          .nullable()
          .optional(),
        space_details: z
          .object({
            id: z.union([z.number(), z.string()]),
            name: z.string().optional(),
            neighborhood: z.string().nullable().optional(),
            cityname: z.string().nullable().optional(),
            address: z.string().nullable().optional(),
            latitude: z.string().nullable().optional(),
            longitude: z.string().nullable().optional(),
            enabled: z.boolean().nullable().optional(),
            type: z.number().nullable().optional(),
            acknowledge_date: z.string().nullable().optional(),
          })
          .nullable()
          .optional(),
        detailed_score_analysis: z
          .array(
            z.object({
              criterion: z.string(),
              requested: z.any(),
              offered: z.any(),
              matched: z.boolean(),
            }),
          )
          .nullable()
          .optional(),
        score_details: z.any().optional(),
      }),
    )
    .optional(),
  proposal_long_url: z.string().nullable().optional(),
  proposal_short_url: z.string().nullable().optional(),
  user_id: z.string(),
  deal_id: z.string().nullable().optional(),
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
  filters: z.any().optional(),
});

const extractionStatusSchema = z.enum(["pending", "ready", "failed"]);

const importDealResponseSchema = z.object({
  // Absent on backends that still extract synchronously — consumers treat
  // undefined as "ready".
  extraction_status: extractionStatusSchema.optional(),
  // A deal with no company attached in HubSpot comes back as null ("" from
  // older backend versions — normalized to null here so consumers only ever
  // see one "missing" representation). The form requires manual fill then.
  company_name: z
    .string()
    .nullable()
    .transform((value) => value || null),
  industry: z.string().nullable().optional(),
  primary_contact_email: z.string().nullable().optional(),
  primary_contact_name: z.string().nullable().optional(),
  // HubSpot company `numberofemployees`; absent on backend versions that
  // predate the field. Feeds diagnosis.numberOfEmployees (SMB gate/WhatsApp
  // routing on the public proposal). Coerced because HubSpot serializes the
  // field as a string (see `parseEmployeeCount` in
  // mappers/proposal-from-api.ts, on the read-back path) — a
  // strict z.number() here would fail the whole import over the headcount.
  company_size: z.coerce.number().nullable().optional(),
  confidence: z.number(),
  summary_narrative: z.string(),
  deal_insights: z.array(z.string()).optional().default([]),
  hard_constraints: z.object({
    city: z.string(),
    min_capacity: z.number().nullable(),
    max_capacity: z.number().nullable(),
    min_budget: z.number().nullable().optional(),
    max_budget: z.number().nullable(),
    mandatory_amenities: z.array(z.string()),
    latitude: z.number().nullable().optional(),
    longitude: z.number().nullable().optional(),
    radius_km: z.number().nullable().optional(),
  }),
  deal_context: z
    .object({
      deal_allocated_budget: z.string(),
    })
    .optional(),
  soft_preferences: z.object({
    preferred_neighborhoods: z.array(z.string()),
    nice_to_have_amenities: z.array(z.string()),
    mobility_needs: z.array(z.string()),
    infrastructure_needs: z.array(z.string()),
  }),
  cultural_fit: z.object({
    company_vibe: z.string(),
    work_model: z.string().nullable(),
    deal_breakers: z.array(z.string()),
  }),
});

const importDealBriefStatusSchema = z.object({
  status: extractionStatusSchema,
  brief: importDealResponseSchema.nullable(),
});

export interface SpaceRatingRequestBody {
  rating: "up" | "down" | null;
  reasons: string[];
  sub_reasons: string[];
  comment: string | null;
  context: "proposal" | "visit_scheduled";
  /** E-mail typed at the proposal email gate — self-reported, not verified.
   * Lets the feedback dashboard split lead vs. internal ratings. */
  rated_by_email: string | null;
}

export { mapSpaceRatings };

export const visionService = {
  async searchMode(
    params: import("@/services/private-office-service/types").SearchParams,
  ): Promise<{
    spaces: RankedSpace[];
    extraSpaces: RankedSpace[];
    outdatedSpaces: RankedSpace[];
    recommendationHash: string | null;
    roomCombinations?: import("@/features/vision-360/ai-mode/hooks/useSearchAndRank").RoomCombination[];
  }> {
    const response = await privateOfficeService.searchByMode(params);
    const rawResults = response.results ?? [];
    const rawExtraRooms = response.extra_rooms ?? [];
    // Map both results and extra_rooms through the same mapper so all fields are consistent
    const spaces = mapSearchResultToRankedSpaces(rawResults);
    const extraSpaces = mapSearchResultToRankedSpaces(rawExtraRooms);
    type RawComboRoom = {
      private_room_id?: string;
      label?: string;
      capacity?: number;
      monthly_price?: number;
    };
    type RawCombo = {
      combination_id?: string;
      space_id?: string | number;
      space_name?: string;
      neighborhood?: string;
      distance_km?: number;
      rooms?: RawComboRoom[];
      total_capacity?: number;
      total_monthly_price?: number;
      score?: number;
    };
    const rawCombinations: RawCombo[] = response.room_combinations ?? [];
    const roomCombinations = rawCombinations.map((c) => ({
      combinationId: c.combination_id ?? "",
      spaceId: String(c.space_id ?? ""),
      spaceName: c.space_name ?? "",
      neighborhood: c.neighborhood ?? "",
      distanceKm: c.distance_km ?? 0,
      rooms: (c.rooms ?? []).map((r) => ({
        privateRoomId: r.private_room_id ?? "",
        label: r.label ?? "",
        capacity: r.capacity ?? 0,
        monthlyPrice: r.monthly_price ?? 0,
      })),
      totalCapacity: c.total_capacity ?? 0,
      totalMonthlyPrice: c.total_monthly_price ?? 0,
      score: c.score ?? 0,
    }));
    return {
      spaces,
      extraSpaces,
      outdatedSpaces: [],
      recommendationHash: null,
      roomCombinations,
    };
  },

  async searchAndRank(
    body: SearchAndRankBody,
    options?: {
      searchSource?: SearchSource | null;
      includeUnavailableWithinPeriod?: number;
    },
  ): Promise<{
    spaces: RankedSpace[];
    outdatedSpaces: RankedSpace[];
    recommendationHash: string | null;
  }> {
    try {
      if (shouldUseFiltersEndpoint(options?.searchSource)) {
        const hardConstraints = body.hard_constraints;
        const cityFull = hardConstraints.city?.trim() || "default";
        const ufMatch = hardConstraints.city?.match(/- ([A-Z]{2}),?/);
        const preferredNeighborhoods =
          body.soft_preferences?.preferred_neighborhoods;
        const isNeighborhoodsSearch =
          Array.isArray(preferredNeighborhoods) &&
          preferredNeighborhoods.length > 0;
        const demandKey = isNeighborhoodsSearch ? "neighborhoods" : cityFull;
        const params: SearchPrivateOfficesParams = {
          demands: {
            [demandKey]: {
              latitude:
                !isNeighborhoodsSearch && hardConstraints.latitude != null
                  ? String(hardConstraints.latitude)
                  : undefined,
              longitude:
                !isNeighborhoodsSearch && hardConstraints.longitude != null
                  ? String(hardConstraints.longitude)
                  : undefined,
              distance: hardConstraints.radius_km ?? undefined,
              minCapacity: hardConstraints.min_capacity ?? undefined,
              maxCapacity: hardConstraints.max_capacity ?? undefined,
              minPrice: hardConstraints.min_budget ?? null,
              maxPrice: hardConstraints.max_budget ?? null,
              privateOfficeStatus: [0],
              categoryIds: getCategoryIdFromPropertyType("private-office"),
              uf: ufMatch?.[1],
              unitCreditPrice:
                body.unit_credit_price ??
                body.hard_constraints?.unit_credit_price ??
                80,
              searchType: isNeighborhoodsSearch ? "neighborhoods" : "location",
              location: isNeighborhoodsSearch
                ? undefined
                : cityFull === "default"
                  ? undefined
                  : cityFull,
              cityName: isNeighborhoodsSearch
                ? hardConstraints.city
                : undefined,
              neighborhood: isNeighborhoodsSearch
                ? preferredNeighborhoods
                : undefined,
              skipRecentUpdatesFilter: false,
              approvalStatus: PrivateOfficeApprovalStatus.approved,
              includeLegacy: false,
              includeDeleted: false,
              ...(options?.includeUnavailableWithinPeriod
                ? {
                    includeUnavailableWithinPeriod:
                      options.includeUnavailableWithinPeriod,
                  }
                : {}),
            },
          },
        };
        const response =
          await privateOfficeService.searchPrivateOffices(params);
        const rawResults: ResultItem[] = response.results ?? [];
        const maxResultsToMap = 50;
        const freshResults = rawResults
          .filter((item) => !isFiltersResultOutdated(item))
          .slice(0, maxResultsToMap);
        const outdatedResults = rawResults.filter((item) =>
          isFiltersResultOutdated(item),
        );
        const spaces = mapFiltersResultToRankedSpaces(freshResults);
        const outdatedSpaces = mapFiltersResultToRankedSpaces(outdatedResults);
        return { spaces, outdatedSpaces, recommendationHash: null };
      }

      const response = await apiVision.post(
        "/api/v1/proposals/search-and-rank",
        body,
      );
      const raw = response.data as Record<string, unknown>;
      const recommendationHash =
        typeof raw.recommendation_hash === "string"
          ? raw.recommendation_hash
          : null;

      // New response format: { top_picks, top_picks_details, other_active, ... }
      if (raw && !Array.isArray(raw) && raw.top_picks_details !== undefined) {
        type TopPick = {
          office_id: string;
          match_score: number;
          sales_pitch: string;
          match_reasons: string[];
        };
        type DetailItem = Record<string, unknown>;

        const topPicks: TopPick[] = Array.isArray(raw.top_picks)
          ? (raw.top_picks as TopPick[])
          : [];
        const topPicksDetails: DetailItem[] = Array.isArray(
          raw.top_picks_details,
        )
          ? (raw.top_picks_details as DetailItem[])
          : [];
        const otherActive: DetailItem[] = Array.isArray(raw.other_active)
          ? (raw.other_active as DetailItem[])
          : [];

        const pitchMap = new Map(
          topPicks.map((p) => [
            p.office_id,
            {
              score: p.match_score,
              pitch: p.sales_pitch,
              reasons: p.match_reasons,
            },
          ]),
        );

        const mapDetail = (item: DetailItem): RankedSpace => {
          const officeId = String(item.office_id ?? item.id ?? "");
          const pitch = pitchMap.get(officeId);
          let monthly =
            item.monthly_price != null ? Number(item.monthly_price) : null;
          const cap = Number(item.capacity ?? 0);
          const itemOrigin =
            typeof item.origin === "string" ? item.origin : null;
          const itemSource =
            typeof item.source === "string" ? item.source : null;
          if (
            itemOrigin === "integration" &&
            itemSource === "vip-office" &&
            (!monthly || monthly === 0) &&
            cap > 0
          ) {
            const pricePerPos = getVipOfficePricePerPosition(
              String(item.name ?? ""),
              String(item.space_name ?? item.name ?? ""),
              String(item.neighborhood ?? ""),
              String(item.city ?? ""),
            );
            if (pricePerPos > 0) monthly = pricePerPos * cap;
          }
          const photos = Array.isArray(item.photos)
            ? (item.photos as string[])
            : [];
          const rawPartnerId = item.partner_id ?? item.space_id;
          const partnerId =
            rawPartnerId != null ? Number(rawPartnerId) : undefined;
          return {
            id: officeId,
            partnerId,
            searchId: item.search_id != null ? Number(item.search_id) : null,
            spaceName: String(item.space_name ?? item.name ?? ""),
            roomName: String(item.name ?? ""),
            city: String(item.city ?? ""),
            neighborhood: String(item.neighborhood ?? "") || undefined,
            address: [item.neighborhood, item.city].filter(Boolean).join(", "),
            lat: Number(item.latitude ?? 0),
            lng: Number(item.longitude ?? 0),
            pricePerWorkstation:
              monthly && cap > 0 ? Math.round(monthly / cap) : 0,
            monthlyPrice: monthly,
            credits: item.credits != null ? Number(item.credits) : null,
            capacity: cap,
            sizeM2: null,
            score:
              pitch?.score ??
              Math.round(Number(item.similarity_score ?? 0) * 100),
            amenities: Array.isArray(item.amenities)
              ? (item.amenities as string[])
              : typeof item.amenities === "string" && item.amenities
                ? item.amenities
                    .split(",")
                    .map((s) => s.trim())
                    .filter(Boolean)
                : [],
            spaceAmenities: (() => {
              const raw = item.space_amenities ?? item.spaceAmenities;
              if (Array.isArray(raw)) return raw as string[];
              if (typeof raw === "string" && raw)
                return raw
                  .split(",")
                  .map((s) => s.trim())
                  .filter(Boolean);
              return undefined;
            })(),
            imageUrl: photos[0] ?? "",
            images: photos,
            // Building (common-area) photos — same data the filters search maps from
            // `space_details.photos`. Without them the "Espaço" gallery starts empty.
            spacePhotos: Array.isArray(item.space_photos)
              ? (item.space_photos as string[]).filter(Boolean)
              : [],
            origin: itemOrigin,
            source: itemSource,
            salesPitch: pitch?.pitch,
            matchReasons: pitch?.reasons,
          };
        };

        return {
          spaces: [
            ...topPicksDetails.map(mapDetail),
            ...otherActive.map(mapDetail),
          ],
          outdatedSpaces: [],
          recommendationHash,
        };
      }

      // Legacy response format: array or { spaces, results }
      const items: Record<string, unknown>[] = Array.isArray(raw)
        ? (raw as Record<string, unknown>[])
        : ((raw?.spaces ?? raw?.results ?? []) as Record<string, unknown>[]);
      return {
        spaces: items.map((item) => ({
          id: String(item.id ?? ""),
          partnerId:
            item.partner_id != null ? Number(item.partner_id) : undefined,
          spaceName: String(item.space_name ?? item.name ?? ""),
          roomName: String(item.room_name ?? item.roomName ?? item.name ?? ""),
          city: String(item.city ?? item.cityname ?? ""),
          address: String(item.address ?? ""),
          lat: Number(item.lat ?? item.latitude ?? 0),
          lng: Number(item.lng ?? item.longitude ?? 0),
          pricePerWorkstation: Number(
            item.price_per_workstation ?? item.pricePerWorkstation ?? 0,
          ),
          monthlyPrice:
            item.monthly_price != null ? Number(item.monthly_price) : null,
          credits: item.credits != null ? Number(item.credits) : null,
          capacity: Number(item.capacity ?? 0),
          sizeM2: item.size_m2 != null ? Number(item.size_m2) : null,
          score: Number(item.score ?? 0),
          amenities: Array.isArray(item.amenities)
            ? (item.amenities as string[])
            : typeof item.amenities === "string" && item.amenities
              ? item.amenities
                  .split(",")
                  .map((s) => s.trim())
                  .filter(Boolean)
              : [],
          imageUrl: String(
            item.image_url ?? item.imageUrl ?? item.picture_url ?? "",
          ),
          images: Array.isArray(item.images) ? (item.images as string[]) : [],
          origin: typeof item.origin === "string" ? item.origin : null,
        })),
        outdatedSpaces: [],
        recommendationHash,
      };
    } catch (error) {
      console.error("Error in searchAndRank:", error);
      throw error;
    }
  },

  async importDeal(
    dealId: string,
    options?: { forceRefresh?: boolean },
  ): Promise<ImportDealResponse> {
    try {
      const response = await apiVision.post("/api/v1/proposals/import-deal", {
        deal_id: dealId,
        // Explicit re-imports bypass the backend's golden-brief cache so a
        // deal fixed in HubSpot (e.g. company attached) reflects immediately.
        ...(options?.forceRefresh ? { force_refresh: true } : {}),
      });
      return importDealResponseSchema.parse(response.data);
    } catch (error) {
      console.error("Error importing deal:", error);
      throw error;
    }
  },

  async getImportDealBrief(dealId: string): Promise<ImportDealBriefStatus> {
    try {
      const response = await apiVision.get(
        `/api/v1/proposals/import-deal/${encodeURIComponent(dealId)}/brief`,
        // Sem timeout, uma request pendurada nunca resolve e o backstop do
        // hook (que só checa o elapsed entre respostas) não dispara.
        { timeout: 10_000 },
      );
      return importDealBriefStatusSchema.parse(response.data);
    } catch (error) {
      console.error("Error fetching import deal brief:", error);
      throw error;
    }
  },

  async getLongListDetail(longListId: string): Promise<LongListDetailResponse> {
    try {
      const response = await apiVision.get(
        `/api/v1/backoffice/long-list/${longListId}`,
      );
      const parsedData = longListDetailSchema.parse(response.data);
      return parsedData;
    } catch (error) {
      console.error("Error fetching long list detail:", error);
      throw error;
    }
  },

  async completeTtoMetric(dealId: string, userId: string): Promise<void> {
    await apiVision.post("/api/v1/metrics/tto/complete", {
      deal_id: dealId,
      user_id: userId,
    });
  },

  /**
   * Audit log of one quick search (PED-12650). Identity travels in the body
   * because apiVision authenticates with a service key, not a user token.
   * Callers fire-and-forget (.catch with reportError) — a log failure must
   * never block the search UX.
   */
  async logQuickSearch(payload: QuickSearchLogPayload): Promise<void> {
    await apiVision.post("/api/v1/quick-searches", payload);
  },

  async getPois(partnerIds: number[]): Promise<PointOfInterest[]> {
    if (!partnerIds.length) return [];
    const params = new URLSearchParams();
    partnerIds.forEach((id) => params.append("space_ids", String(id)));
    const response = await apiVision.get(
      `/api/v1/backoffice/pois?${params.toString()}`,
    );
    const data = response.data as {
      results?: Array<{
        space_id: number | string;
        pois?: Array<{
          poi_name: string;
          poi_latitude: number;
          poi_longitude: number;
          poi_category: string;
          poi_description?: string;
          poi_address?: string;
        }>;
      }>;
    };
    const pois: PointOfInterest[] = [];
    let idx = 0;
    for (const result of data.results ?? []) {
      for (const poi of result.pois ?? []) {
        pois.push({
          id: idx++,
          name: poi.poi_name,
          category: poi.poi_category as POICategory,
          lat: poi.poi_latitude,
          lng: poi.poi_longitude,
          description: poi.poi_description,
          address: poi.poi_address,
        });
      }
    }
    return pois;
  },

  async createLongList(
    payload: CreateLongListPayload,
  ): Promise<z.infer<typeof longListResponseSchema>> {
    try {
      const cleanedPayload: CreateLongListPayload = { ...payload };
      if (cleanedPayload.filters) {
        const cleanedFilters = { ...cleanedPayload.filters };
        const numericFields = [
          "budgetMin",
          "budgetMax",
          "sizeMin",
          "sizeMax",
          "positionsMin",
          "positionsMax",
        ] as const;

        numericFields.forEach((field) => {
          const value = cleanedFilters[field as keyof typeof cleanedFilters];
          if (value === "" || value === null || value === undefined) {
            delete cleanedFilters[field as keyof typeof cleanedFilters];
          }
        });

        cleanedPayload.filters = cleanedFilters;
      }

      const response = await apiVision.post(
        "/api/v1/backoffice/long-list",
        cleanedPayload,
      );

      const parsedData = longListResponseSchema.parse(response.data);

      return parsedData;
    } catch (error) {
      console.error("Error creating long list:", error);
      throw error;
    }
  },

  async createProposal(
    payload: CreateProposalBody,
  ): Promise<CreateProposalResponse> {
    // Temporary fix to return the legacy proposal URL for the AI-mode wizard.
    // smb proposal will replace the existing legacy proposal URL in the near future.
    const response = await apiVision.post("/api/v1/proposals", payload, {
      params: { returnLegacyProposal: true },
    });
    return response.data as CreateProposalResponse;
  },

  async updateProposal(
    proposalId: string,
    payload: UpdateProposalBody,
  ): Promise<UpdateProposalResult> {
    try {
      const response = await apiVision.patch(
        `/api/v1/proposals/${proposalId}`,
        payload,
      );
      const data = response.data as Record<string, unknown>;
      const noChanges = !!data.no_changes;
      try {
        const proposal = await this.getProposal(proposalId);
        return { proposal, noChanges };
      } catch {
        return { noChanges };
      }
    } catch (err: unknown) {
      if (isAxiosError(err) && err.response?.status === 409) {
        const currentVersion =
          (
            err.response.data as {
              detail?: { current_version?: number };
            }
          )?.detail?.current_version ?? null;
        throw new VersionConflictError(currentVersion);
      }
      throw err;
    }
  },

  async listProposals(
    params?: ProposalListParams,
  ): Promise<ListProposalsResponse> {
    const response = await apiVision.get<ListProposalsResponse>(
      "/api/v1/proposals",
      { params },
    );
    return {
      proposals: response.data.proposals ?? [],
      total: response.data.total ?? 0,
      skip: response.data.skip ?? 0,
      limit: response.data.limit ?? 10,
    };
  },

  async listExecutives(): Promise<ListExecutivesResponse> {
    const response = await apiVision.get<ListExecutivesResponse>(
      "/api/v1/proposals/executives",
      { params: { force_refresh: false } },
    );
    return response.data;
  },

  async getProposalListItem(uuid: string): Promise<ProposalListItem> {
    const response = await apiVision.get<ProposalListItem>(
      `/api/v1/proposals/${uuid}`,
    );
    return response.data;
  },

  async getProposal(proposalId: string): Promise<ProposalPayload> {
    const response = await apiVision.get<GetProposalApiResponseV2>(
      `/api/v1/proposals/${proposalId}`,
    );
    const apiData = response.data;

    const firstSpace = (apiData.selected_spaces ?? [])[0] as
      | Record<string, unknown>
      | undefined;
    const isNewFormat = firstSpace != null && "spaceName" in firstSpace;

    if (isNewFormat) return mapNewFormatProposal(apiData, proposalId);

    const allRecommendedIds = apiData.recommended_space_ids ?? [];
    const totalSpaces = (apiData.selected_spaces ?? []).length;
    const maxRecommended = Math.ceil(totalSpaces * 0.3);
    const recommendedIds = new Set(allRecommendedIds.slice(0, maxRecommended));

    const suggestedSpaces: RankedSpace[] = (apiData.selected_spaces ?? []).map(
      (s) => {
        const cap = s.capacity || 0;
        const sd = s.space_details;

        const neighborhood = sd?.neighborhood ?? s.neighborhood ?? "";
        const rawCity =
          sd?.cityname ?? s.cityname ?? apiData.hard_constraints.city ?? "";
        const city = rawCity.includes("/")
          ? rawCity.split("/")[0].trim()
          : rawCity;
        const rawSdAddress = sd?.address ?? null;
        const sdAddress = rawSdAddress
          ? (() => {
              const parts = rawSdAddress.split(" - ");
              parts[0] = parts[0].replace(/,\s*\d+.*$/, "").trim();
              return parts.join(" - ");
            })()
          : null;
        const address =
          sdAddress ?? (neighborhood ? `${neighborhood}, ${city}` : "");
        const spaceName = sd?.name ?? s.space_name ?? "";

        const origin = s.details?.origin ?? null;
        const source = s.details?.source ?? null;
        const isVipOffice = origin === "integration" && source === "vip-office";

        const rawMonthly = s.monthly_price ?? null;
        const rawClientPrice = s.clientPrice ?? 0;
        let monthly: number | null =
          rawClientPrice > 0 ? rawClientPrice : rawMonthly;
        if (isVipOffice && (!monthly || monthly === 0) && cap > 0) {
          const pricePerPos = getVipOfficePricePerPosition(
            s.label,
            spaceName,
            neighborhood,
            rawCity,
          );
          monthly = pricePerPos > 0 ? pricePerPos * cap : null;
        }

        const credits =
          isVipOffice && monthly != null && monthly > 0
            ? Math.ceil(monthly / 80)
            : (s.creditsQuantity ?? null);

        const lat = sd?.latitude
          ? Number(sd.latitude)
          : s.space_latitude
            ? Number(s.space_latitude)
            : s.latitude
              ? Number(s.latitude)
              : 0;
        const lng = sd?.longitude
          ? Number(sd.longitude)
          : s.space_longitude
            ? Number(s.space_longitude)
            : s.longitude
              ? Number(s.longitude)
              : 0;

        let images: string[];
        if (s.images && s.images.length > 0) {
          images = s.images;
        } else if (s.details) {
          images = [
            s.details.picture_url,
            s.details.picture_url1,
            s.details.picture_url2,
            s.details.picture_url3,
            s.details.picture_url4,
            s.details.picture_url5,
          ].filter(
            (url): url is string => typeof url === "string" && url.length > 0,
          );
        } else {
          images = [];
        }

        const amenitiesRaw = s.amenities ?? sd?.amenities ?? "";
        const amenitiesList =
          typeof amenitiesRaw === "string" && amenitiesRaw
            ? amenitiesRaw
                .split(",")
                .map((a) => a.trim())
                .filter(Boolean)
            : [];

        const score =
          s.relevance_score ??
          (s.match_percent != null ? Math.round(Number(s.match_percent)) : 0);

        const spaceId = s.private_room_id ?? s.id ?? "";

        return {
          id: spaceId,
          searchId: s.search_id ?? null,
          partnerId: s.space_id ? Number(s.space_id) : undefined,
          spaceName,
          roomName: s.label,
          city,
          address,
          lat,
          lng,
          pricePerWorkstation:
            monthly && cap > 0 ? Math.round(monthly / cap) : 0,
          monthlyPrice: monthly,
          credits,
          capacity: cap,
          sizeM2: s.size ?? null,
          score,
          amenities: amenitiesList,
          imageUrl: images[0] ?? "",
          images,
          spacePhotos: s.spacePhotos ?? [],
          selectedImages: s.selectedImages,
          selectedSpacePhotos: s.selectedSpacePhotos,
          groupIds: s.groupIds,
          isIndividual: s.isIndividual,
          isRecommended: recommendedIds.has(spaceId),
          origin,
          source,
          availabilityDate: s.availabilityDate ?? null,
        };
      },
    );

    const suggestedById = new Map(suggestedSpaces.map((sp) => [sp.id, sp]));

    const spaceAdjustments = (apiData.selected_spaces ?? []).map((s) => {
      const sId = s.private_room_id ?? s.id ?? "";
      const mapped = suggestedById.get(sId);
      const isVipOfficeMapped =
        mapped?.origin === "integration" && mapped?.source === "vip-office";
      const effectivePrice =
        mapped?.monthlyPrice ?? s.clientPrice ?? s.monthly_price ?? 0;
      const effectiveCredits = mapped?.credits ?? s.creditsQuantity ?? 0;

      const rawSetupCost = s.setupPrice ?? 0;
      const setupCost =
        isVipOfficeMapped && rawSetupCost === 0 && effectivePrice > 0
          ? Math.round(effectivePrice * 0.08)
          : rawSetupCost;
      const rawSetupCredits = s.setupCredits ?? 0;
      const setupCredits =
        isVipOfficeMapped && rawSetupCredits === 0 && setupCost > 0
          ? Math.ceil(setupCost / 80)
          : rawSetupCredits;

      return {
        spaceId: sId,
        extraValue: 0,
        margin: 0,
        clientPrice: effectivePrice,
        credits: effectiveCredits,
        extraCredits: 0,
        totalCredits: effectiveCredits,
        totalPrice:
          s.totalPriceFinal && s.totalPriceFinal > 0
            ? s.totalPriceFinal
            : effectivePrice,
        setupCost,
        setupCredits,
        marketDiscountPct:
          s.market_discount_pct != null
            ? Number(s.market_discount_pct)
            : undefined,
      };
    });

    const validityDays = Number(
      apiData.proposal_validity ?? apiData.expiresAt ?? 7,
    );

    const rawCreatedAt = apiData.createdAt;
    const createdAtStr =
      rawCreatedAt != null && typeof rawCreatedAt === "object"
        ? (rawCreatedAt as { $date: string }).$date
        : ((rawCreatedAt as string) ?? new Date().toISOString());

    const expiresAtTimestamp =
      new Date(createdAtStr).getTime() + validityDays * 24 * 60 * 60 * 1000;

    // Rebuild the selection split from what persists on each space (see new-format path).
    const restoredCombos = reconstructRoomCombinations(suggestedSpaces);
    const restoredIndividualIds = suggestedSpaces.some(
      (s) => s.isIndividual != null,
    )
      ? suggestedSpaces.filter((s) => s.isIndividual).map((s) => s.id)
      : (apiData.selected_office_ids ?? []);

    const payload: ProposalPayload = {
      uuid: proposalId,
      generatedAt: createdAtStr,
      suggestedSpaces,
      spaceRatings: mapSpaceRatings(apiData.space_ratings),
      proposalType: apiData.proposal_type as ProposalPayload["proposalType"],
      proposalOrigin: apiData.proposal_origin,
      createdBy: apiData.created_by
        ? {
            userId: apiData.created_by.user_id,
            email: apiData.created_by.email,
            fullName: apiData.created_by.full_name,
          }
        : undefined,
      versionNumber: apiData.version_number,
      updatedBy: apiData.updated_by
        ? {
            userId: apiData.updated_by.user_id,
            email: apiData.updated_by.email,
            fullName: apiData.updated_by.full_name,
          }
        : undefined,
      updatedAt: apiData.updated_at ?? undefined,
      versions: apiData.versions?.map((v) => ({
        versionNumber: v.version_number,
        snapshotAt: v.snapshot_at,
        updatedBy: v.updated_by
          ? {
              userId: v.updated_by.user_id,
              email: v.updated_by.email,
              fullName: v.updated_by.full_name,
            }
          : null,
        snapshot: v.snapshot,
        diffs: v.diffs,
      })),
      state: {
        currentStep: 3,
        completedSteps: [0, 1, 2, 3],
        diagnosis: {
          dealId: apiData.deal_context.deal_id ?? "",
          companyName: apiData.deal_context.company_name ?? "",
          contactName: (apiData.deal_context.contact_name as string) ?? "",
          contactEmail: (apiData.deal_context.contact_email as string) ?? "",
          contactPhone: (apiData.deal_context.contact_phone as string) ?? "",
          numberOfEmployees: parseEmployeeCount(
            apiData.deal_context.numberofemployees,
          ),
          segment: (apiData.deal_context.segment as string) ?? "",
          targetCity: apiData.hard_constraints.city ?? "",
          positionsMin: apiData.hard_constraints.capacity_min ?? "",
          positionsMax: apiData.hard_constraints.capacity_max ?? "",
          monthlyBudget:
            apiData.hard_constraints.budget_max != null
              ? String(apiData.hard_constraints.budget_max)
              : "",
          needs: apiData.summary_narrative ?? "",
          selectedLocation:
            apiData.location_lat != null && apiData.location_lng != null
              ? {
                  label:
                    apiData.location_label ??
                    apiData.hard_constraints.city ??
                    "",
                  lat: Number(apiData.location_lat),
                  lng: Number(apiData.location_lng),
                }
              : null,
        },
        location:
          apiData.location_lat != null && apiData.location_lng != null
            ? {
                location: {
                  label:
                    apiData.location_label ??
                    apiData.hard_constraints.city ??
                    "",
                  lat: Number(apiData.location_lat),
                  lng: Number(apiData.location_lng),
                },
                radiusKm: apiData.radius_km ?? null,
                selectedSpaceIds: restoredIndividualIds,
                selectedComboIds: restoredCombos.map((c) => c.combinationId),
                rankedSpaces: suggestedSpaces,
                roomCombinations: restoredCombos,
                recommendationHash: apiData.recommendationHash ?? null,
              }
            : null,
        adjustments: {
          creditsPrice: apiData.credits_price ?? 0,
          contractMonths: (apiData.contract_months ?? null) as
            | 6
            | 12
            | 24
            | 36
            | null,
          spaceAdjustments,
          proposalValidity: expiresAtTimestamp,
          sellerNotes: apiData.seller_notes ?? "",
          recommendedSpaceIds: apiData.recommended_space_ids ?? [],
          selectedSpaces: suggestedSpaces,
          includeFlexCredits: apiData.include_flex_credits ?? false,
          flexCredits: apiData.flex_credits,
          showLicenseFee: apiData.show_license_fee ?? false,
          licenseFeeMode: apiData.license_fee_mode,
          licenseFeePercentage: apiData.license_fee_percentage,
          licenseFeeFixed: apiData.license_fee_fixed,
          licenseFeeValue: apiData.license_fee_value,
          licenseFeePeriod: apiData.license_fee_period,
          includeSetupFee: apiData.include_setup_fee ?? false,
          setupFee: apiData.setup_fee,
        },
      },
    };

    return payload;
  },

  async chatSend(
    body: Record<string, string>,
  ): Promise<{ conversation_id: string; job_id: string }> {
    const res = await apiVision.post<{
      conversation_id: string;
      job_id: string;
    }>("/api/v1/chat/send", body);
    return res.data;
  },

  async chatPoll(
    jobId: string,
    convId: string,
  ): Promise<{ done: boolean; answer: string | null }> {
    const res = await apiVision.get<{ done: boolean; answer: string | null }>(
      "/api/v1/chat/poll",
      { params: { job_id: jobId, conversation_id: convId } },
    );
    return res.data;
  },

  notifySlack(messageType: string, message: string) {
    if (import.meta.env.VITE_NODE_ENV !== "production") {
      console.info("[notifySlack] skipped in non-production — payload:", {
        message_type: messageType,
        message,
      });
      return;
    }
    apiVision
      .post("/api/v1/slack/send-message", {
        message_type: messageType,
        message,
      })
      .catch((error) => {
        reportError(error, { operation: "notifySlack" });
      });
  },

  async createTicketRequest(payload: TicketRequestPayload): Promise<void> {
    if (import.meta.env.VITE_NODE_ENV !== "production") {
      console.info(
        "[createTicketRequest] skipped in non-production — payload:",
        payload,
      );
      return;
    }
    await apiVision.post("/api/v1/tickets/open", payload);
  },

  /** Upsert da avaliação do cliente para um espaço da proposta (PUT idempotente
   * por proposta+espaço — reavaliar sobrescreve). Roda em todo ambiente:
   * diferente de Slack/tickets, o endpoint de rating existe em staging. */
  async rateSpace(
    proposalUuid: string,
    spaceId: string,
    body: SpaceRatingRequestBody,
  ): Promise<void> {
    await apiVision.put(
      `/api/v1/proposals/${proposalUuid}/spaces/${spaceId}/rating`,
      body,
    );
  },
};

export * from "./types";
export { apiVision } from "./api-vision";
